/**
 * `/overlay` — OBS Browser Source mount.
 *
 * Reads every overlay knob from `window.location.search` and mounts the
 * visual-engine's `<OverlayRoute>` over a full-viewport transparent /
 * chromakey background. Identical contract to the old Astro
 * `/overlay` page; streamers can drop the URL straight into OBS.
 */
import { useEffect, useState } from "react";
import {
  OverlayRoute,
  parseOverlayParamsString,
  type OverlayRouteParams,
} from "@pilot-protocol/visual-engine";

const DEFAULT_PANELS = [
  "metadata",
  "statA",
  "statB",
  "reasoningA",
  "reasoningB",
  "telemetry",
  "accolades",
] as const;

export default function OverlayPage() {
  const [params, setParams] = useState<OverlayRouteParams | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const search =
      typeof window !== "undefined" ? window.location.search : "";
    setParams(parseOverlayParamsString(search));
    const tickRaw = new URLSearchParams(search).get("tick");
    const n = tickRaw ? parseInt(tickRaw, 10) : 0;
    setTick(Number.isFinite(n) && n >= 0 ? n : 0);
  }, []);

  if (params === null) return null;

  return (
    <OverlayRoute
      matchUrl={params.matchUrl}
      transcriptUrlA={params.transcriptUrlA}
      transcriptUrlB={params.transcriptUrlB}
      authToken={params.authToken}
      panels={params.panels ?? DEFAULT_PANELS}
      background={params.background}
      showReasoningStats={params.reasoningStats}
      showZoneLegend={params.zoneLegend}
      telemetryMaxRows={params.telemetryMaxRows ?? undefined}
      tick={tick}
    />
  );
}
